
import { StudentEvent } from "../types";
import { format } from "date-fns";
import { enUS, ptBR } from "date-fns/locale";

export const getEventColor = (event: StudentEvent): string => {
  // For cancelled events
  if (event.status === 'cancelled' || event.status === 'rejected') {
    return "bg-gray-200 text-gray-600 border border-gray-300";
  }

  // For pending cancellation
  if (event.status === 'pending_cancellation') {
    return "bg-yellow-100 text-yellow-800 border border-yellow-200";
  }

  // For approved requests that became scheduled classes (green)
  if (event.type === 'approved_request' || (event.status === 'scheduled' && event.isCurrentStudent)) {
    return "bg-green-100 text-green-800 border border-green-200";
  }

  // Online sessions have a different color
  if (event.event_type === 'online_session' || event.is_online) {
    return "bg-blue-100 text-blue-800 border border-blue-200";
  }

  // Training sessions
  if (event.event_type === 'training_session') {
    if (event.type === 'request') {
      return "bg-yellow-100 text-yellow-800 border border-yellow-200";
    }
    if (event.isCurrentStudent) {
      return "bg-green-100 text-green-800 border border-green-200";
    }
    return "bg-gray-100 text-gray-800 border border-gray-300";
  }

  // Assessments
  if (event.event_type === 'assessment') {
    return "bg-purple-100 text-purple-800 border border-purple-200";
  }

  // Pending requests - only yellow for current student's requests
  if (event.type === 'request' && event.status === 'pending' && event.isCurrentStudent) {
    return "bg-yellow-100 text-yellow-800 border border-yellow-200";
  }

  // Default (including pending requests from other students)
  return "bg-gray-100 text-gray-800 border border-gray-300";
};

export const getEventStatus = (event: StudentEvent, t: any): string => {
  if (event.status === 'cancelled') {
    return t("eventDialog.classCancelled");
  }

  if (event.status === 'rejected') {
    return t("eventDialog.requestRejected");
  }

  if (event.status === 'pending_cancellation') {
    return t("eventDialog.cancelPending");
  }

  // For approved requests that became scheduled classes
  if (event.type === 'approved_request' || (event.status === 'scheduled' && event.type === 'approved_request')) {
    return event.title; // Will be "AULA", "CLASS", etc.
  }

  if (event.type === 'request' && event.status === 'pending') {
    return t("eventDialog.awaitingApproval");
  }

  // Show online designation for online classes
  if (event.event_type === 'online_session' || event.is_online) {
    return event.title || (event.language === 'pt' ? 'Aula Online' : 'Online Class');
  }

  if (!event.isCurrentStudent) {
    return t("eventDialog.occupied");
  }

  return event.title || '';
};

export const showCancelledOverlay = (event: StudentEvent): boolean => {
  return event.status === 'cancelled' || 
         event.status === 'rejected' || 
         event.status === 'pending_cancellation';
};

// Add the missing formatEventDate function
export const formatEventDate = (dateString: string, language: string): string => {
  const date = new Date(dateString);
  const locale = language === 'pt' ? ptBR : enUS;
  
  // Format: "Monday, January 1, 2023 at 10:00 AM" or "Segunda-feira, 1 de janeiro de 2023 às 10:00"
  if (language === 'pt') {
    return format(date, "EEEE, d 'de' MMMM 'de' yyyy 'às' HH:mm", { locale });
  } else {
    return format(date, "EEEE, MMMM d, yyyy 'at' h:mm a", { locale });
  }
};
